import type { FC } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';

export type HeadMetaProps = {
  /** meta data for <meta /> tags. */
  meta?: {
    title?: string;
    description?: string;
    keywords?: string;
    robots?: string;
  };
  /** links for <link /> tags. */
  links?: {
    alternateLinks?: {
      [x: string]: string;
    } | null;
    canonical?: string;
  };
  /** global schema.org data to be rendered in <script> tag. */
  schemaorg?:
    | string
    | {
        [x: string]: string | { [k: string]: string } | undefined;
      }[];
};

/**
 * HeadMeta component renders title, meta, links and global schema.org data in the document head.
 * */
export const HeadMeta: FC<HeadMetaProps> = ({ meta, links, schemaorg }) => {
  const { asPath, locale: language } = useRouter();
  const { title, description, keywords, robots } = meta || {};
  const {
    alternateLinks,
    canonical = typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}`
      : asPath.split('?')[0],
  } = links || {};

  return (
    <Head>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {robots && <meta name="robots" content={robots} />}
      {alternateLinks &&
        Object.keys(alternateLinks).map((alternateLanguage) =>
          alternateLanguage !== language ? (
            <link
              key={alternateLanguage}
              rel="alternate"
              hrefLang={alternateLanguage}
              href={alternateLinks[alternateLanguage]}
            />
          ) : null,
        )}
      {canonical && <link href={canonical} rel="canonical" />}
      {schemaorg &&
        (typeof schemaorg === 'string' ? (
          <script
            type="application/ld+json"
            key="schemaorg"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: schemaorg }}
          />
        ) : (
          schemaorg.map((schemaJSON, index) => (
            <script
              type="application/ld+json"
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaJSON) }}
            />
          ))
        ))}
    </Head>
  );
};
